import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AlertColor, Box, Card, Grid } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import MediumTypography from '../../components/formlib/MediumTypography';
import Textinput from '../../components/formlib/Textinput';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import dayjs, { Dayjs } from 'dayjs';
import * as yup from 'yup';
import SelectComponent from '../../components/formlib/SelectComponent';
import { Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { AddNewReferral, OptionType } from '../../utils/type';
import ModalPopup from '../../components/formlib/ModalPopup';
import {
  addOrUpdateReferral,
  addOrUpdateReferralDraft,
  getDraftById,
} from '../../services/configApi/User';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import '../ClientsDashboardPage.css';
import { ReferralNavStateProps } from './ReferralDashboard';
import {
  CLIENT_NAME_KEY,
  EMAIL_REGEX,
  REFERRAL_ID_KEY,
  REFERRAL_PROGRAM_KEY,
  REFERRAL_STATUS_KEY,
  SELECTED_PROGRAM_KEY,
  STORAGE_USER_ID_KEY,
} from '../../services/Constant';
import _, { debounce } from 'lodash';
import {
  ApiError,
  ApiMessage,
  isCustomError,
} from '../../services/ApiResponseHandler';
import CustomRadioButton, {
  RadioButtonData,
} from '../../components/formlib/CustomRadioButton';
import {
  ReferralParent,
  ReferralParentComponent,
} from './ReferralParentComponent';
import { useIntl } from 'react-intl';
import { getFullName } from '../../utils/nameUtils';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import TitleText from '../../components/formlib/TitleText';
import CustomBackArrow from '../../components/imagepickers/backArrow';
import {
  datePickerMinDateMaxDateValidate,
  threeYearsAgo,
} from '../../utils/dateUtil';
import {
  CLIENTS_ROUTE_NAME,
  REFERRAL_DASHBOARD_ROUTE_NAME,
} from '../../routes/Routing';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import CommentTextbox from '../../components/formlib/CommentTextbox';
import { LookUpContext } from '../../context/LookUpContextProvider';
import {
  convertGenderToBackend,
  convertGenderToFrontend,
} from '../../utils/genderUtil';
import AddressForm from '../../components/formlib/AddressForm';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import {
  ReferralResponseType,
  getAllReferrals,
} from '../../services/configApi/Clients';
import SimilarClientsModal from './SimilarClientsModal';
import { SimilarClient } from './type';
import {
  nameValidation as suffixValidation,
  formatPhoneNumber,
} from '../../utils/formValidationUtil';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';

const addressSchema = yup.object().shape({
  address1: yup.string().required('addressLine1RequiredMessage'),
  address2: yup.string(),
  zipCode: yup
    .string()
    .min(5, 'validZipCodeErrorMessage')
    .required('zipCodeRequiredMessage'),
  city: yup.object().shape({
    label: yup.string().required('cityRequiredMessage'),
  }),
  state: yup.string().required('stateRequiredMessage'),
});

const parentsSchema = yup.object().shape({
  firstName: yup.string().required('ParentFirstName.Validation'),
  lastName: yup.string().required('ParentLastName.Validation'),
  email: yup
    .string()
    .required('parent.emailRequired')
    .test('email-validation', 'EmailFormat.Validation', function (value) {
      if (value && value !== 'None') {
        const emailRegex = /^[^\s@]{1,64}@[A-Za-z0-9.-]{1,255}\.[A-Za-z]{2,}$/;
        return value !== '' && emailRegex.test(value);
      } else if (value === 'None') {
        return true;
      } else {
        return false;
      }
    }),
  relationShipToClient: yup.string().required('parentRelationship.Validation'),
  mobilePhoneNumber: yup.string().min(12, 'PhoneNumber.Validation').nullable(),
  homePhoneNumber: yup.string().min(12, 'PhoneNumber.Validation').nullable(),
  workPhoneNumber: yup.string().min(12, 'PhoneNumber.Validation').nullable(),
  address: addressSchema,
});

const validationSchema = yup.object().shape({
  firstName: yup.string().required('Firstname.Validation'),
  lastName: yup.string().required('Lastname.Validation'),
  middleName: yup.string(),
  suffix: yup.string().nullable(),
  sex: yup.string().required('AddNewReferralDetails.gender.Validation'),
  dateOfBirth: yup.date().required('DateOfBirth.Validation').nullable(),
  address1: yup.string().required('addressLine1RequiredMessage'),
  address2: yup.string(),
  zipCode: yup
    .string()
    .min(5, 'validZipCodeErrorMessage')
    .required('zipCodeRequiredMessage'),
  city: yup.object().shape({
    label: yup.string().required('cityRequiredMessage'),
  }),
  state: yup.string().required('stateRequiredMessage'),
  referralBeginDate: yup
    .date()
    .required('AddNewReferralDetails.referralBeginDate.Validation')
    .nullable(),
  referralEndDate: yup.date().nullable(),
  parents: yup.array().of(parentsSchema),
  referralContact: yup.object().shape({
    referralContactDate: yup
      .date()
      .required('AddNewReferralDetails.referralContactDate.Validation')
      .nullable(),
    referralContactName: yup.string(),
    referralContactEmail: yup
      .string()
      .matches(EMAIL_REGEX, 'EmailFormat.Validation'),
    referralContactJobTitle: yup.string(),
    referralContactType: yup.string(),
    referralContactPhone: yup.string(),
    referralContactFax: yup.string().min(12, 'FaxFormat.Validation'),
    referralSource: yup.string().required('referralSource.Validation'),
    referralReason: yup.string().required('referralReason.Validation'),
  }),
  notes: yup.string(),
  referralType: yup
    .string()
    .required('AddNewReferralDetails.referralType.Validation'),
  // referralDiagnosisCode: yup.string().nullable(),
});

const validateDateOfBirth = (dateOfBirth: string | Dayjs) => {
  if (dayjs(dateOfBirth).isBefore(threeYearsAgo().format('MM/DD/YYYY'))) {
    return 'dob3YearValidationMessage';
  }
  if (dayjs(dateOfBirth).isAfter(dayjs().format('MM/DD/YYYY'))) {
    return 'dobFutureDateMessage';
  }
  if (datePickerMinDateMaxDateValidate(dateOfBirth)) {
    return 'datePickerMinDateMaxDateValidate';
  }
  return null;
};

const validateForm = (values: AddNewReferral) => {
  const errors: Partial<AddNewReferral> = {};
  if (values.dateOfBirth) {
    const error = validateDateOfBirth(values.dateOfBirth);
    if (error) {
      errors.dateOfBirth = error;
    }
  }

  if (values.referralBeginDate) {
    if (dayjs(values.referralBeginDate).isAfter(dayjs().format('MM/DD/YYYY'))) {
      errors.referralBeginDate = 'notFutureDateMessage';
    }
  }

  if (values.referralContact.referralContactDate) {
    if (
      dayjs(values.referralContact.referralContactDate).isBefore(
        values.dateOfBirth,
      ) ||
      dayjs(values.referralContact.referralContactDate).isAfter(
        dayjs().format('MM/DD/YYYY'),
      )
    ) {
      errors.referralContact = errors.referralContact || {
        referralContactDate: '',
        referralContactPhone: '',
        referralContactFax: '',
        referralContactEmail: '',
        referralContactType: '',
        referralContactJobTitle: '',
        referralContactName: '',
        referralSource: '',
        referralReason: '',
        isDcf: false,
        notes: '',
      };
      errors.referralContact.referralContactDate = 'validDate';
    }
    if (
      datePickerMinDateMaxDateValidate(
        values.referralContact.referralContactDate,
      )
    ) {
      errors.referralContact = errors.referralContact || {
        referralContactDate: '',
        referralContactPhone: '',
        referralContactFax: '',
        referralContactEmail: '',
        referralContactType: '',
        referralContactJobTitle: '',
        referralContactName: '',
        referralSource: '',
        referralReason: '',
        isDcf: false,
        notes: '',
      };
      errors.referralContact.referralContactDate =
        'datePickerMinDateMaxDateValidate';
    }
  }

  if (values.referralBeginDate) {
    if (datePickerMinDateMaxDateValidate(values.referralBeginDate)) {
      errors.referralBeginDate = 'datePickerMinDateMaxDateValidate';
    }
  }

  return errors;
};

const nameValidation = (value: string): string | undefined => {
  const trimmedValue = value?.replace(/^\s+/, '');
  const regex = /^[A-Za-z!,'. -]*$/;
  if (regex.test(trimmedValue)) {
    return trimmedValue;
  }
};

export const referralTypeOptions: RadioButtonData[] = [
  { defaultLabel: 'New', labelId: 'newText', value: 'New' },
  { defaultLabel: 'EIPP Child', labelId: 'eippChildText', value: 'EIPP Child' },
  { defaultLabel: 'EIPP Mom', labelId: 'eippMomText', value: 'EIPP Mom' },
];

const referrals: AddNewReferral = {
  savedAsDraft: false,
  id: '',
  firstName: '',
  lastName: '',
  middleName: '',
  suffix: null,
  sex: '',
  dateOfBirth: null,
  eligibilityDate: null,
  address1: '',
  address2: '',
  zipCode: '',
  city: { id: '', label: '' },
  state: '',
  ifspDueDate: null,
  referralBeginDate: null,
  referralEndDate: null,
  referralType: 'New',
  ageInMonths: '',
  createdBy: '',
  parents: [
    {
      firstName: '',
      lastName: '',
      email: '',
      mobilePhoneNumber: '',
      homePhoneNumber: '',
      workPhoneNumber: '',
      relationShipToClient: '',
      address: {
        address1: '',
        address2: '',
        zipCode: '',
        city: { id: '', label: '' },
        state: '',
      },
      isAddressSameAsClientAddress: false,
    },
  ],
  similarClients: [],
  clientId: null,
  dphId: null,
  referralContact: {
    referralContactDate: null,
    referralContactName: '',
    referralContactType: 'otherText',
    referralContactJobTitle: '',
    referralContactEmail: '',
    referralContactFax: '',
    referralContactPhone: '',
    referralSource: '',
    referralReason: '',
    isDcf: false,
    notes: '',
  },
  isAddressSameAsClientAddress: false,
  // referralDiagnosisCode: null,
};

const AddNewReferralDetails = () => {
  const rctl = useIntl();
  const [openModal, setOpenModal] = React.useState(false);
  const formRef = React.useRef<FormikProps<AddNewReferral>>(null);
  const [open, setOpen] = React.useState(false);
  const [toastrVariable, setToastrVariable] =
    React.useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = React.useState('');
  const [toastrId, setToastrId] = React.useState('');
  const [actionButton, setActionButton] = React.useState(true);
  const [titleText, setTitleText] = useState<string>('');
  const [showSimilarClientsModal, setShowSimilarClientsModal] =
    useState<boolean>(false);
  const [similarClients, setSimilarClients] = useState<SimilarClient[]>([]);
  const [pointerEvents, setPointerEvents] = useState<'none' | 'auto'>('auto');
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { lookups } = useContext(LookUpContext);
  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);
  const [referralReasons, setReferralReasons] = useState<OptionType[]>([]);
  const [referralSources, setReferralSources] = useState<OptionType[]>([]);
  const [dcfChecked, setDcfChecked] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    getDraftDetails();
  }, []);

  useEffect(() => {
    if (lookups === null) {
      return;
    }
    setReferralReasons(() => {
      return lookups.referralReason.map((reason) => {
        return {
          label: reason.description,
          id: reason.code,
        };
      });
    });
    setReferralSources(() => {
      return lookups.referralSource.map((source) => {
        return {
          label: source.description,
          id: source.code,
        };
      });
    });
  }, [lookups]);

  const handleAddAsClient = useCallback(
    debounce((values: AddNewReferral) => {
      const allParentsHavePhone = values.parents.every(
        (parent) =>
          parent.mobilePhoneNumber?.trim() || parent.homePhoneNumber?.trim(),
      );

      if (allParentsHavePhone) {
        saveReferralAndNavigate(values, false, false);
      } else {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('error');
        setToastrId('parentGuardian.numberRequired');
        setToastrDefaultMessage(
          'Enter at least one phone number : Cell or Home',
        );
      }
    }, 1000),
    [],
  );

  const handleDraft = useCallback(
    debounce((values: AddNewReferral) => {
      if (
        values.firstName == '' ||
        values.lastName == '' ||
        values.sex == '' ||
        values.dateOfBirth == null
      ) {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('error');
        setToastrId('Referral.saveAsDraftError');
        setToastrDefaultMessage(
          'First name, Last name, sex and date of birth is required to save the referral as draft.',
        );
      } else {
        saveDraftReferralAndNavigate(values);
      }
    }, 1000),
    [],
  );

  const Gender = ['Female', 'Male', 'Unassigned'];
  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const getDraftDetails = () => {
    if (localStorage.getItem('referralId')) {
      toggleLoader(true);
      getDraftById()
        .then((response: AddNewReferral) => {
          toggleLoader(false);
          response.city =
            response.city !== null
              ? ({
                  id: 'asd',
                  label: response.city as string,
                } as OptionType)
              : { id: '', label: '' };
          response.parents = response.parents.map((parent) => {
            return {
              ...parent,
              address: {
                ...parent.address,
                city:
                  parent.address.city !== null && parent.address.city !== ''
                    ? { id: 'aa', label: parent.address.city as string }
                    : { id: 'aa', label: '' },
              },
            };
          });
          setDcfChecked(response.referralContact.isDcf);
          response.sex = convertGenderToFrontend(response.sex) ?? '';
          formRef.current?.setValues(response, true);
        })
        .catch((error) => {
          toggleLoader(false);
          setOpen(true);
          setToastrVariable('error');
          setToastrId('failed');
          setToastrDefaultMessage(error);
        });
    }
  };

  const dateExtension = (date: string) => {
    const originalDate = new Date(date);
    originalDate.setDate(originalDate.getDate() + 45);
    const month = (originalDate.getMonth() + 1).toString().padStart(2, '0');
    const day = originalDate.getDate().toString().padStart(2, '0');
    const year = originalDate.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const saveReferralAndNavigate = (
    request: AddNewReferral,
    createDuplicateClient: boolean,
    skipSimilarClientCheck: boolean,
  ) => {
    toggleLoader(true);
    const userId = localStorage.getItem(STORAGE_USER_ID_KEY);
    if (userId) {
      request.createdBy = userId;
    }
    request.firstName = request.firstName.trim();
    request.middleName = request.middleName.trim();
    request.lastName = request.lastName.trim();
    request.suffix = request.suffix?.trim() ?? null;
    request.sex = convertGenderToBackend(request.sex) ?? '';
    request.referralContact.referralContactType = rctl.formatMessage({
      id: request.referralContact.referralContactType,
    });
    request.parents.forEach((parent) => {
      parent.address.city = (parent.address.city as OptionType).label;
    });
    request.city = (request.city as OptionType).label;

    addOrUpdateReferral(request, createDuplicateClient, skipSimilarClientCheck)
      .then((response: AddNewReferral) => {
        if (response.similarClients?.length > 0) {
          response.similarClients.forEach((item) => (item.isChecked = false));
          toggleLoader(false);
          setShowSimilarClientsModal(true);
          setSimilarClients(response.similarClients);
          // Will be removed in future if there is duplicate functionality
          // formRef.current?.setValues({
          //   ...formRef.current?.values,
          //   duplicateClient: response.duplicateClient,
          // });
          // setTimeout(() => {
          //   toggleLoader(false);
          //   setShowDuplicateModel(true);
          // }, 200);
        } else {
          setShowSimilarClientsModal(false);
          setOpen(true);
          setToastrVariable('success');
          setToastrId('Referral.addsuccess');
          setToastrDefaultMessage('Referral added successfully');
          setTimeout(() => {
            toggleLoader(false);
            navigateToClientCreation(response);
          }, 1000);
        }
      })
      .catch((error) => {
        handleSaveReferralError(error, request.id);
      });
  };

  const saveDraftReferralAndNavigate = (request: AddNewReferral) => {
    toggleLoader(true);
    const userId = localStorage.getItem(STORAGE_USER_ID_KEY);
    if (userId) {
      request.createdBy = userId;
    }
    request.firstName = request.firstName.trim();
    request.middleName = request.middleName.trim();
    request.lastName = request.lastName.trim();
    request.suffix = request.suffix?.trim() ?? null;
    request.id = request.id ? request.id : '';
    request.sex = convertGenderToBackend(request.sex) ?? '';
    request.referralContact.referralContactType = rctl.formatMessage({
      id: request.referralContact.referralContactType,
    });
    request.parents.forEach((parent) => {
      parent.address.city = (parent.address.city as OptionType).label;
    });
    request.city = (request.city as OptionType).label;

    addOrUpdateReferralDraft(request)
      .then(() => {
        setOpen(true);
        setToastrVariable('success');
        setToastrId('Referral.draftSuccess');
        setToastrDefaultMessage('Draft saved successfully');
        setTimeout(() => {
          toggleLoader(false);
          navigate(REFERRAL_DASHBOARD_ROUTE_NAME);
        }, 1000);
      })
      .catch((error) => {
        handleSaveReferralError(error, request.id);
      });
  };

  const handleSaveReferralError = (
    error: ApiError | ApiMessage | object,
    id: string,
  ) => {
    toggleLoader(false);
    setToastrVariable('error');
    setOpen(true);
    if (isCustomError(error)) {
      const apiError = error as ApiError;
      setToastrId(apiError.id);
      setToastrDefaultMessage(apiError.message);
    } else {
      const response = error as ApiMessage;
      if (response.code === 409) {
        setToastrDefaultMessage(
          'This referral is added to the system with the same referral date. Choose a different referral date.',
        );
        setToastrId('referralConflictMessage');
      } else {
        setToastrId(
          id === '' ? 'Referral.addFailure' : 'Referral.updateFailure',
        );
        setToastrDefaultMessage(
          id === '' ? 'Failed to add referral' : 'Failed to update referral',
        );
      }
    }
  };

  const getAllReferralsBasedOnSelectedClient = async (
    clientId: string,
    defaultReferralId: string,
  ) => {
    toggleLoader(true);
    if (clientId) {
      await getAllReferrals(clientId, defaultReferralId)
        .then(async (response: ReferralResponseType[]) => {
          if (response) {
            localStorage.setItem(
              'clientReferralDetails',
              JSON.stringify(response),
            );
          }
          toggleLoader(false);
        })
        .catch((error) => {
          toggleLoader(false);
          setOpen(true);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('clientLoadError');
            setToastrDefaultMessage('Failed to get client details');
          }
        });
    }
  };

  const navigateToClientCreation = async (values: AddNewReferral) => {
    if (values.clientId !== null) {
      sessionStorage.setItem('tabName', '0');
      localStorage.setItem('ClientId', values.clientId);
      localStorage.setItem(
        CLIENT_NAME_KEY,
        getFullName(values.firstName.trim(), values.lastName.trim()),
      );
      localStorage.setItem(REFERRAL_ID_KEY, values.id);
      localStorage.setItem('defaultReferralId', values.id);
      await getAllReferralsBasedOnSelectedClient(values.clientId, values.id);
      if (checkPermissionForFeature('backend.clients', 'viewPermission')) {
        localStorage.setItem(REFERRAL_STATUS_KEY, 'Active');
        const selectedProgram = localStorage.getItem(SELECTED_PROGRAM_KEY);
        if (selectedProgram) {
          localStorage.setItem(
            REFERRAL_PROGRAM_KEY,
            JSON.parse(selectedProgram).id,
          );
        }
        navigate(CLIENTS_ROUTE_NAME, {
          replace: true,
          state: { clientId: values.clientId, isCreate: true },
        });
      } else {
        navigate(REFERRAL_DASHBOARD_ROUTE_NAME);
      }
    }
  };

  useEffect(() => {
    const stateParams = location.state as ReferralNavStateProps;
    if (stateParams) {
      const { editable, data } = stateParams;
      if (editable) {
        setActionButton(true);
        setPointerEvents('auto');
        setTitleText('Referraldashboard.EditTitletext');
      } else {
        setActionButton(false);
        setPointerEvents('none');
        setTitleText('Referraldashboard.ViewTitletext');
      }
      formRef.current?.setValues(data);
    } else {
      setActionButton(true);
      setPointerEvents('auto');
      setTitleText('Referraldashboard.AddTitletext');
    }
  }, []);

  useEffect(() => {
    if (_.isEqual(formRef.current?.initialValues, formRef.current?.values)) {
      handleUpdateUnsavedChanges(false);
    } else {
      handleUpdateUnsavedChanges(true);
    }
  }, [formRef.current?.values]);

  useEffect(() => {
    const handleFormChange = () => {
      if (_.isEqual(formRef.current?.initialValues, formRef.current?.values)) {
        handleUpdateUnsavedChanges(false);
      } else {
        handleUpdateUnsavedChanges(true);
      }
    };

    // Add event listeners for both keyboard and mouse events
    window.addEventListener('keydown', handleFormChange);
    window.addEventListener('click', handleFormChange);

    return () => {
      // Remove both event listeners during cleanup
      window.removeEventListener('keydown', handleFormChange);
      window.removeEventListener('click', handleFormChange);
    };
  }, []);

  return (
    <Box component="main" style={{ pointerEvents: pointerEvents }}>
      {showSimilarClientsModal && (
        <SimilarClientsModal
          showModal={showSimilarClientsModal}
          onModalClose={() => {
            setShowSimilarClientsModal(false);
          }}
          referralBeginDate={formRef.current?.values?.referralBeginDate ?? null}
          data={similarClients}
          addNewReferral={(id) => {
            const values = JSON.parse(
              JSON.stringify(formRef.current?.values),
            ) as AddNewReferral;
            if (values) {
              values.clientId = id;
              saveReferralAndNavigate(values, false, true);
            }
          }}
          addNewClient={() => {
            const values = JSON.parse(
              JSON.stringify(formRef.current?.values),
            ) as AddNewReferral;
            if (values) {
              saveReferralAndNavigate(values, true, true);
            }
          }}
        />
      )}
      <SnackBarComponent
        open={open}
        handleClose={handleClose}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <Box component="section">
        <Box className="rowContainer">
          <CustomBackArrow
            onClick={() => navigate(REFERRAL_DASHBOARD_ROUTE_NAME)}
          />
          <Box component="div" className="ml-md">
            <TitleText labelid={titleText} defaultlabel="Referral" />
          </Box>
        </Box>
        <Box component="div">
          <Formik
            innerRef={formRef}
            validateOnMount={true}
            enableReinitialize={true}
            initialValues={referrals}
            validateOnBlur={true}
            validateOnChange={false}
            validationSchema={validationSchema}
            validate={validateForm}
            onSubmit={(values) => {
              toggleLoader(true);
              const request = JSON.parse(
                JSON.stringify(values),
              ) as AddNewReferral;
              handleAddAsClient(request);
            }}
          >
            {({
              setFieldValue,
              handleChange,
              handleSubmit,
              handleBlur,
              values,
              errors,
              touched,
              initialValues,
            }) => (
              <Box component="div" onBlur={handleBlur}>
                <Card className="formCardview">
                  <Grid
                    container
                    rowSpacing={'24px'}
                    columnSpacing={'40px'}
                    className="mb-lg"
                  >
                    <Grid item xs={12} lg={12}>
                      <MediumTypography
                        className="mainText-lg"
                        labelid="QuestionnaireClientInfo.SubHeaderOnetext2"
                        defaultlabel="Referral Information"
                      />
                    </Grid>
                    <Grid item xs={4.5} lg={4.5}>
                      <Textinput
                        name="firstName"
                        labelid="AddNewReferralDetails.First"
                        defaultlabelid="First Name"
                        Value={values.firstName}
                        Required={true}
                        inputProps={{
                          maxLength: 40,
                        }}
                        handlechange={(value: string) => {
                          const validatedValue = nameValidation(value);
                          if (validatedValue !== undefined) {
                            setFieldValue('firstName', validatedValue);
                          }
                        }}
                      />

                      {errors.firstName && touched.firstName && (
                        <MediumTypography
                          labelid={errors.firstName}
                          defaultlabel="First Name is Required"
                          className="errorText-md"
                        />
                      )}
                    </Grid>
                    <Grid item xs={4.5} lg={4.5}>
                      <Textinput
                        name="middleName"
                        Value={values.middleName}
                        labelid="Clientpage.Middle"
                        defaultlabelid="Middle Name"
                        inputProps={{
                          maxLength: 20,
                        }}
                        handlechange={(value: string) => {
                          const validatedValue = nameValidation(value);
                          if (validatedValue !== undefined) {
                            setFieldValue('middleName', validatedValue);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={4.5} lg={4.5}>
                      <Textinput
                        name="lastName"
                        Value={values.lastName}
                        labelid="AddNewReferralDetails.Last"
                        defaultlabelid="Last Name"
                        Required={true}
                        inputProps={{
                          maxLength: 40,
                        }}
                        handlechange={(value: string) => {
                          const validatedValue = nameValidation(value);
                          if (validatedValue !== undefined) {
                            setFieldValue('lastName', validatedValue);
                          }
                        }}
                      />
                      {errors.lastName && touched.lastName && (
                        <MediumTypography
                          labelid={errors.lastName}
                          defaultlabel="Last Name is Required"
                          className="errorText-md"
                        />
                      )}
                    </Grid>

                    <Grid item xs={4.5} lg={4.5}>
                      <Textinput
                        name="suffix"
                        Value={values.suffix ?? ''}
                        labelid="AddNewReferralDetails.Suffix"
                        defaultlabelid="Suffix"
                        inputProps={{
                          maxLength: 10,
                        }}
                        handlechange={(value: string) => {
                          setFieldValue('suffix', suffixValidation(value));
                        }}
                      />
                    </Grid>

                    <Grid item xs={4.5} lg={4.5}>
                      <SelectComponent
                        labelid="Clientpage.Sex"
                        defaultlabelid="sex"
                        name="sex"
                        value={values.sex}
                        handleChange={(value: string) => {
                          setFieldValue('sex', value);
                        }}
                        names={Gender}
                        Required
                      />
                      {errors.sex && touched.sex && (
                        <MediumTypography
                          labelid={errors.sex}
                          className="errorText-md"
                        />
                      )}
                    </Grid>
                    <Grid item xs={4.5} lg={4.5}>
                      <DatePickerComponent
                        value={dayjs(values.dateOfBirth)}
                        disableFuture={true}
                        minDate={threeYearsAgo()}
                        maxDate={dayjs()}
                        labelid="AddNewReferralDetails.dateofbirth"
                        defaultlabelid="Date of Birth"
                        handlechange={(date: Dayjs | null) => {
                          const formattedDate =
                            dayjs(date).format('MM/DD/YYYY');
                          if (date === null) {
                            formRef.current?.setValues({
                              ...values,
                              dateOfBirth: null,
                              referralContact: {
                                ...values.referralContact,
                                referralContactDate: null,
                              },
                            });
                          } else {
                            formRef.current?.setValues({
                              ...values,
                              dateOfBirth: formattedDate,
                              referralContact: {
                                ...values.referralContact,
                                referralContactDate: null,
                              },
                            });
                          }
                        }}
                        required={true}
                      />
                      {errors.dateOfBirth && touched.dateOfBirth && (
                        <MediumTypography
                          labelid={errors.dateOfBirth}
                          className="errorText-md"
                        />
                      )}
                    </Grid>

                    <Grid item xs={4.5} lg={4.5}>
                      <DatePickerComponent
                        value={dayjs(values.referralBeginDate)}
                        labelid="AddNewReferralDetails.referralBeginDate"
                        disableFuture={true}
                        defaultlabelid="Referral Begin Date"
                        handlechange={(date: Dayjs | null) => {
                          const formattedDate =
                            dayjs(date).format('MM/DD/YYYY');
                          if (date === null) {
                            formRef.current?.setValues({
                              ...values,
                              referralBeginDate: null,
                            });
                          } else {
                            formRef.current?.setValues({
                              ...values,
                              referralBeginDate: formattedDate,
                            });
                          }
                        }}
                        required={true}
                      />

                      {errors.referralBeginDate &&
                        touched.referralBeginDate && (
                          <MediumTypography
                            labelid={errors.referralBeginDate}
                            className="errorText-md"
                          />
                        )}
                    </Grid>

                    <Grid item xs={4.5} lg={4.5} display={'none'}>
                      <DatePickerComponent
                        value={dayjs(values.referralEndDate)}
                        disableFuture={false}
                        minDate={dayjs()}
                        labelid="AddNewReferralDetails.referralEndDate"
                        defaultlabelid="Referral End Date"
                        handlechange={(date: Dayjs | null) => {
                          const formattedDate =
                            dayjs(date).format('MM/DD/YYYY');
                          if (date === null) {
                            formRef.current?.setValues({
                              ...values,
                              referralEndDate: null,
                            });
                          } else {
                            formRef.current?.setValues({
                              ...values,
                              referralEndDate: formattedDate,
                            });
                          }
                        }}
                      />
                    </Grid>
                    <Grid container item xs={9}>
                      <AddressForm
                        address1PlaceholderId="address1Text"
                        address2PlaceholderId="address2Text"
                        zipCodePlaceholderId="Clientpage.Zip"
                        cityPlaceholderId="Clientpage.City"
                        statePlaceholderId="Clientpage.State"
                        value={{
                          address1: values.address1,
                          address2: values.address2,
                          zipCode: values.zipCode,
                          city: values.city,
                          state: values.state,
                        }}
                        isClient={true}
                        disableForm={false}
                        formRequired={true}
                        formikErrors={{
                          address1: errors.address1,
                          address2: errors.address2,
                          zipCode: errors.zipCode,
                          city: errors.city,
                          state: errors.state,
                        }}
                        formikTouched={{
                          address1: touched.address1,
                          address2: touched.address2,
                          zipCode: touched.zipCode,
                          city: touched.city,
                          state: touched.state,
                        }}
                        handleAddressUpdate={(newAddress) => {
                          values.parents.forEach((parent) => {
                            if (parent.isAddressSameAsClientAddress) {
                              parent.address = newAddress;
                            }
                          });
                          formRef.current?.setValues({
                            ...values,
                            address1: newAddress.address1,
                            address2: newAddress.address2,
                            zipCode: newAddress.zipCode,
                            city: newAddress.city,
                            state: newAddress.state,
                          });
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Box className="horizontalLine" />

                  <Grid container rowSpacing={'24px'} columnSpacing={'40px'}>
                    <Grid item xs={12} display={'flex'} alignItems={'center'}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <MediumTypography
                            labelid="AddNewReferralDetails.referralType"
                            defaultlabel="Referral Type"
                            sxProps={{
                              color: '#2A4241',
                              fontWeight: 'bold',
                              fontSize: '14px',
                              marginRight: '16px',
                            }}
                          />
                          <CustomRadioButton
                            data={referralTypeOptions}
                            value={values.referralType}
                            onSelectItem={(value) => {
                              if (value !== null) {
                                setFieldValue('referralType', value);
                                if (value !== 'Incoming Transfer') {
                                  setFieldValue('elProgram', '');
                                  setFieldValue('elContactNumber', '');
                                  setFieldValue(
                                    'remainingAssessmentHoursDec',
                                    null,
                                  );
                                  setFieldValue('beginDate', null);
                                  setFieldValue('eligibilityDate', '');
                                  setFieldValue('rollOverDate', null);
                                }
                              }
                            }}
                            disable
                          />
                        </Box>
                        {errors.referralType && touched.referralType && (
                          <MediumTypography
                            labelid={errors.referralType}
                            className="errorText-md"
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  <Box className="horizontalLine" />

                  <Grid container rowSpacing={'24px'} columnSpacing={'40px'}>
                    <Grid item>
                      <ReferralParentComponent
                        headerTitleId={
                          'AddNewReferralDetails.ParentInformation'
                        }
                        defaultHeaderTitle={'Guardian Information'}
                        formikErrors={formRef.current?.values.parents.map(
                          (parent, index) => {
                            if (parent.isAddressSameAsClientAddress) {
                              const parentErrors = errors.parents?.[index] as
                                | FormikErrors<ReferralParent>
                                | undefined;

                              return {
                                email: parentErrors?.email,
                                firstName: parentErrors?.firstName,
                                mobilePhoneNumber:
                                  parentErrors?.mobilePhoneNumber,
                                homePhoneNumber: parentErrors?.homePhoneNumber,
                                workPhoneNumber: parentErrors?.workPhoneNumber,
                                isAddressSameAsClientAddress:
                                  parentErrors?.isAddressSameAsClientAddress,
                                lastName: parentErrors?.lastName,
                                relationShipToClient:
                                  parentErrors?.relationShipToClient,
                                address: {
                                  address1: errors.address1,
                                  address2: errors.address2,
                                  city: errors.city,
                                  state: errors.state,
                                  zipCode: errors.zipCode,
                                },
                              } as FormikErrors<ReferralParent>;
                            } else {
                              return errors.parents?.[
                                index
                              ] as FormikErrors<ReferralParent>;
                            }
                          },
                        )}
                        onDeleteClick={(index: number) => {
                          formRef.current?.setValues({
                            ...values,
                            parents: values.parents.filter(
                              (__, i) => i !== index,
                            ),
                          });
                        }}
                        formikTouched={formRef.current?.values.parents.map(
                          (parent, index) => {
                            if (parent.isAddressSameAsClientAddress) {
                              return {
                                email: touched.parents?.[index]?.email,
                                firstName: touched.parents?.[index]?.firstName,
                                mobilePhoneNumber:
                                  touched.parents?.[index]?.mobilePhoneNumber,
                                homePhoneNumber:
                                  touched.parents?.[index]?.homePhoneNumber,
                                workPhoneNumber:
                                  touched.parents?.[index]?.workPhoneNumber,
                                isAddressSameAsClientAddress:
                                  touched.parents?.[index]
                                    ?.isAddressSameAsClientAddress,
                                lastName: touched.parents?.[index]?.lastName,
                                relationShipToClient:
                                  touched.parents?.[index]
                                    ?.relationShipToClient,
                                address: {
                                  address1: touched.address1,
                                  address2: touched.address2,
                                  city: touched.city,
                                  state: touched.state,
                                  zipCode: touched.zipCode,
                                },
                              } as FormikTouched<ReferralParent>;
                            } else {
                              return touched.parents?.[index];
                            }
                          },
                        )}
                        clientAddress={{
                          address1: values.address1,
                          address2: values.address2,
                          zipCode: values.zipCode,
                          city: values.city,
                          state: values.state,
                        }}
                      />

                      {values.parents.length < 3 && (
                        <ButtonComponent
                          className="btn-add-code mt-md"
                          variantType="contained"
                          type="submit"
                          label={`+ ${rctl.formatMessage({
                            id: 'Parent.addParentText',
                            defaultMessage: 'Add Parent',
                          })}`}
                          defaultLabelId="+ Add Parent"
                          onClick={() => {
                            if (
                              formRef.current !== null &&
                              formRef.current.values.parents.length < 3
                            ) {
                              formRef.current.setValues({
                                ...values,
                                parents: [
                                  ...values.parents,
                                  {
                                    firstName: '',
                                    lastName: '',
                                    email: '',
                                    mobilePhoneNumber: '',
                                    homePhoneNumber: '',
                                    workPhoneNumber: '',
                                    relationShipToClient: '',
                                    address: {
                                      address1: '',
                                      address2: '',
                                      zipCode: '',
                                      city: { id: '', label: '' },
                                      state: '',
                                    },
                                    isAddressSameAsClientAddress: false,
                                  },
                                ],
                              });
                            }
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                  <Box className="horizontalLine"></Box>

                  <MediumTypography
                    labelid="AddNewReferralDetails.referalContactText"
                    defaultlabel="Referral Contact"
                    sxProps={{
                      color: '#2A4241',
                      fontWeight: 'bold',
                      fontSize: '16px',
                      marginBottom: '16px',
                    }}
                  />

                  <Grid container rowSpacing={'24px'} columnSpacing={'40px'}>
                    <Grid item xs={4.5} lg={4.5}>
                      <Textinput
                        name="referralContactName"
                        labelid="AddNewReferralDetails.Name"
                        defaultlabelid="Name"
                        Value={values.referralContact.referralContactName}
                        inputProps={{
                          maxLength: 50,
                        }}
                        handlechange={(value: string) => {
                          const validatedValue = nameValidation(value);
                          if (validatedValue !== undefined) {
                            setFieldValue(
                              'referralContact.referralContactName',
                              validatedValue,
                            );
                          }
                        }}
                      />
                      {errors?.referralContact?.referralContactName &&
                        touched?.referralContact?.referralContactName && (
                          <MediumTypography
                            labelid={
                              errors.referralContact?.referralContactName
                            }
                            defaultlabel="Name is Required"
                            className="errorText-md"
                          />
                        )}
                    </Grid>

                    <Grid item xs={4.5} lg={4.5}>
                      <Textinput
                        name="referralContactJobTitle"
                        Value={values.referralContact.referralContactJobTitle}
                        labelid="AddNewReferralDetails.JobTitle"
                        defaultlabelid="Job Title"
                        inputProps={{
                          maxLength: 50,
                        }}
                        handlechange={(value: string) => {
                          const validatedValue = nameValidation(value);
                          if (validatedValue !== undefined) {
                            setFieldValue(
                              'referralContact.referralContactJobTitle',
                              validatedValue,
                            );
                          }
                        }}
                      />
                      {errors?.referralContact?.referralContactJobTitle &&
                        touched?.referralContact?.referralContactJobTitle && (
                          <MediumTypography
                            labelid={
                              errors.referralContact?.referralContactJobTitle
                            }
                            defaultlabel="Job Title is Required"
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                    <Grid item xs={4.5} lg={4.5} className="pt-none">
                      <Box component="div" className="flex__">
                        <CheckBoxComponent
                          ischecked={dcfChecked}
                          CheckHandleChange={(value) => {
                            setDcfChecked(value.target.checked);
                            setFieldValue(
                              'referralContact.isDcf',
                              value.target.checked,
                            );
                          }}
                          labelid="AddNewReferralDetails.DCF"
                          defaultlabelid="DCF"
                          className="mainText-lg"
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={4.5} lg={4.5}></Grid>

                    <Grid item xs={4.5} lg={4.5}>
                      <DatePickerComponent
                        value={
                          values.referralContact.referralContactDate !== null
                            ? dayjs(values.referralContact.referralContactDate)
                            : null
                        }
                        labelid="AddNewReferralDetails.referralDate"
                        disableFuture={true}
                        minDate={dayjs(values.dateOfBirth)}
                        defaultlabelid="Referral Contact Date"
                        handlechange={(date: Dayjs | null) => {
                          const formattedDate =
                            dayjs(date).format('MM/DD/YYYY');
                          if (date === null) {
                            formRef.current?.setValues({
                              ...values,
                              referralContact: {
                                ...values.referralContact,
                                referralContactDate: null,
                              },
                              ifspDueDate: null,
                            });
                          } else {
                            formRef.current?.setValues({
                              ...values,
                              referralContact: {
                                ...values.referralContact,
                                referralContactDate: formattedDate,
                              },
                              ifspDueDate: dateExtension(formattedDate),
                            });
                          }
                        }}
                        required={true}
                      />
                      {values.ifspDueDate && (
                        <MediumTypography
                          label={`${rctl.formatMessage({
                            id: 'AddNewReferralDetails.ifspDueDate',
                          })}: ${values.ifspDueDate as string}`}
                          fontweight={600}
                          sxProps={{
                            color: '#2A4241',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            mt: '4px',
                          }}
                        />
                      )}
                      {errors.referralContact?.referralContactDate &&
                        touched.referralContact?.referralContactDate && (
                          <MediumTypography
                            labelid={
                              errors.referralContact?.referralContactDate
                            }
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                    <Grid item xs={4.5} lg={4.5}>
                      <DropdownComponent
                        Required
                        names={referralSources}
                        labelid="AddNewReferralDetails.referralSource"
                        defaultlabelid="Referral Source"
                        value={values.referralContact.referralSource}
                        handleChange={handleChange(
                          'referralContact.referralSource',
                        )}
                      />
                      {errors.referralContact?.referralSource &&
                        touched.referralContact?.referralSource && (
                          <MediumTypography
                            labelid={errors.referralContact?.referralSource}
                            defaultlabel="Referral Source is Required"
                            className="errorText-md"
                          />
                        )}
                    </Grid>

                    <Grid item xs={4.5} lg={4.5}>
                      <DropdownComponent
                        Required
                        names={referralReasons}
                        labelid="AddNewReferralDetails.referralReason"
                        defaultlabelid="Referral Reason"
                        value={values.referralContact.referralReason}
                        handleChange={handleChange(
                          'referralContact.referralReason',
                        )}
                      />

                      {errors.referralContact?.referralReason &&
                        touched.referralContact?.referralReason && (
                          <MediumTypography
                            labelid={errors.referralContact?.referralReason}
                            defaultlabel="Referral Reason is Required"
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                    <Grid item xs={4.5} lg={4.5}>
                      <Textinput
                        name="referralContactEmail"
                        Value={values.referralContact.referralContactEmail}
                        labelid="AddNewReferralDetails.Email"
                        defaultlabelid="Email"
                        Required={false}
                        inputProps={{
                          maxLength: 50,
                        }}
                        handlechange={(value: string) => {
                          setFieldValue(
                            `referralContact.referralContactEmail`,
                            value,
                          );
                        }}
                      />
                      {errors.referralContact?.referralContactEmail &&
                        touched.referralContact?.referralContactEmail && (
                          <MediumTypography
                            labelid={
                              errors.referralContact?.referralContactEmail
                            }
                            defaultlabel="Invalid Email Address"
                            className="errorText-md"
                          />
                        )}
                    </Grid>

                    <Grid item xs={4.5} lg={4.5}>
                      <Textinput
                        name="referralContactPhone"
                        Value={values.referralContact.referralContactPhone}
                        labelid="AddNewReferralDetails.referralPhone"
                        defaultlabelid="Phone"
                        inputProps={{
                          maxLength: 50,
                        }}
                        handlechange={(value: string) => {
                          setFieldValue(
                            'referralContact.referralContactPhone',
                            formatPhoneNumber(value),
                          );
                        }}
                      />
                      {errors?.referralContact?.referralContactPhone &&
                        touched?.referralContact?.referralContactPhone && (
                          <MediumTypography
                            labelid={
                              errors.referralContact?.referralContactPhone
                            }
                            defaultlabel="Phone is Required"
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                    <Grid item xs={4.5} lg={4.5}>
                      <Textinput
                        name="referralContactFax"
                        Value={values.referralContact.referralContactFax}
                        labelid="AddNewReferralDetails.Fax"
                        defaultlabelid="Fax"
                        Required={false}
                        inputProps={{
                          maxLength: 50,
                        }}
                        handlechange={(value: string) => {
                          setFieldValue(
                            'referralContact.referralContactFax',
                            formatPhoneNumber(value),
                          );
                        }}
                      />
                      {errors?.referralContact?.referralContactFax &&
                        touched?.referralContact?.referralContactFax && (
                          <MediumTypography
                            labelid={errors.referralContact?.referralContactFax}
                            defaultlabel="Invalid Fax Number"
                            className="errorText-md"
                          />
                        )}
                    </Grid>

                    <Grid item xs={9} lg={9}>
                      <CommentTextbox
                        name="notes"
                        placeholder={rctl.formatMessage({
                          id: 'AddNewReferralDetails.notes',
                          defaultMessage: 'Notes',
                        })}
                        Value={values.referralContact.notes}
                        maxLength={2000}
                        rows={4}
                        handlechange={(value: string) => {
                          setFieldValue('referralContact.notes', value);
                        }}
                      />

                      {errors?.referralContact?.notes &&
                        touched?.referralContact?.notes && (
                          <MediumTypography
                            labelid={errors.referralContact.notes}
                            defaultlabel="Notes is Required"
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                  </Grid>
                </Card>

                {actionButton && (
                  <Box className="rowContainer">
                    <Grid container>
                      <Grid item xs={6} lg={6}>
                        <ButtonComponent
                          className="btn-primary btn-cancel mr-md"
                          variantType="contained"
                          labelId="Save as Draft"
                          defaultLabelId="Save as Draft"
                          onClick={() => {
                            toggleLoader(true);
                            handleDraft(values);
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} lg={6}>
                        <Box
                          component="div"
                          className="flex__ text-align-end justifyContent-FlexEnd"
                        >
                          <ButtonComponent
                            className="btn-primary btn-cancel btn_width_90px mr-md"
                            variantType="contained"
                            labelId="Clientpage.cancelbtn"
                            defaultLabelId="Cancel"
                            onClick={() => {
                              if (_.isEqual(initialValues, values)) {
                                navigate(REFERRAL_DASHBOARD_ROUTE_NAME);
                              } else {
                                setOpenModal(true);
                              }
                            }}
                          />

                          <ButtonComponent
                            className="btn-primary btn-submit"
                            variantType="contained"
                            labelId="addAsClientText"
                            defaultLabelId="Add Referral"
                            onClick={() => {
                              handleSubmit();
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            )}
          </Formik>
        </Box>

        <ModalPopup
          description="formUnsavedChangesMessage"
          open={openModal}
          onCancel={() => {
            setOpenModal(false);
          }}
          onOk={() => {
            formRef.current?.resetForm();
            setOpenModal(false);
            handleUpdateUnsavedChanges(false);
            navigate(REFERRAL_DASHBOARD_ROUTE_NAME);
          }}
          labelId1="Clientpage.Nobtn"
          negativeActionLabel="No"
          labelId2="Clientpage.Yesbtn"
          positiveActionLabel="Yes"
        />
      </Box>
    </Box>
  );
};

export default AddNewReferralDetails;
